import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentType,
} from "react";
import { Empty, Pagination, Select, Spin, message } from "antd";
import type { TablePaginationConfig } from "antd";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { DownloadOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import CustomDatePicker from "../../CommonComponents/CustomDatePicker/CustomDatePicker";
import DataTableBase from "../../shared/dashboards/DataTable/DataTable";
import Input from "../../shared/Input/Input";
import SharedSelect from "../../shared/Select/Select";
import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getEffectiveAdminTimezone } from "../../../utils/dateService";
import { normalizeAntSelectNumberValue } from "../../ReportingSummary/reportingSummaryUtils";
import { PAGE_SIZE_OPTIONS } from "../../ReportingSummary/reportingSummaryConstants";
import { buildCasinoGamesSortParam } from "../../../queries/casino/casinoGamesReportApi";
import { useCasinoGamesReport } from "../../../queries/casino/useCasinoGamesReport";
import { parseCasinoKpiFilterMetadata } from "../../../queries/casino-kpis/casinoKpiFilters";
import {
  type CasinoGamesRow,
  type CasinoGamesSortKey,
  getCasinoGamesColumns,
} from "./casinoGamesColumns";
import { buildCasinoGamesTableCsv } from "./casinoGamesCsv";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import "./stylesCasinoGamesReports.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const DataTable = DataTableBase as ComponentType<Record<string, unknown>>;

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const ALL_VALUE = "all";

function getDefaultGamesDateRange(): [Dayjs, Dayjs] {
  const tz = getEffectiveAdminTimezone();
  return [
    dayjs().tz(tz).startOf("month"),
    dayjs().tz(tz).endOf("day"),
  ];
}

type FilterMeta = {
  key: string;
  multi_select?: boolean;
  options: string[];
  default_values?: string[];
  selected_values?: string[];
};

function extractFilterMeta(
  meta: unknown,
  key: string,
): FilterMeta | null {
  if (!meta || typeof meta !== "object") {
    return null;
  }
  const f = (meta as { filters?: FilterMeta[] }).filters;
  if (!Array.isArray(f)) {
    return null;
  }
  return f.find((x) => x?.key === key) ?? null;
}

type SelectOption = { value: string; label: string };

function toSelectOptions(
  fromApi: string[] | undefined,
  withAll: boolean,
): SelectOption[] {
  const o = (fromApi ?? []).map((s) => ({ value: s, label: s }));
  if (withAll) {
    return [
      { value: ALL_VALUE, label: "All" },
      ...o,
    ];
  }
  return o;
}

function CasinoGamesReports() {
  usePageTitle(AppStrings.CASINO_GAMES_PAGE_TITLE);
  const { setActiveSidebarTab } = useContext(AppContext);

  const [dates, setDates] = useState<[Dayjs, Dayjs]>(getDefaultGamesDateRange);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [draftProvider, setDraftProvider] = useState<string | undefined>(
    undefined,
  );
  const [draftCategory, setDraftCategory] = useState<string | undefined>(
    undefined,
  );

  const [appliedProvider, setAppliedProvider] = useState<string | undefined>(
    undefined,
  );
  const [appliedCategory, setAppliedCategory] = useState<string | undefined>(
    undefined,
  );
  const [draftMoneyTypes, setDraftMoneyTypes] = useState<string[]>([]);
  const [appliedMoneyTypes, setAppliedMoneyTypes] = useState<string[]>([]);

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortState, setSortState] = useState<{
    field: CasinoGamesSortKey | null;
    order: "ascend" | "descend" | null;
  }>({ field: null, order: null });

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(searchText.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(t);
  }, [searchText]);

  const startDate = dates[0]?.format("YYYY-MM-DD");
  const endDate = dates[1]?.format("YYYY-MM-DD");

  const sortParam = useMemo(() => {
    if (!sortState.field || !sortState.order) {
      return undefined;
    }
    return buildCasinoGamesSortParam(sortState.field, sortState.order);
  }, [sortState.field, sortState.order]);

  const reportParams = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      page: currentPage,
      size: pageSize,
      search: debouncedSearch,
      sort: sortParam,
      subprovider: appliedProvider,
      category: appliedCategory,
      money_type: appliedMoneyTypes,
    }),
    [
      startDate,
      endDate,
      currentPage,
      pageSize,
      debouncedSearch,
      sortParam,
      appliedProvider,
      appliedCategory,
      appliedMoneyTypes,
    ],
  );

  const queryEnabled = Boolean(startDate && endDate);
  const { data, isError, isFetching } = useCasinoGamesReport(reportParams, {
    enabled: queryEnabled,
  });

  useEffect(() => {
    if (isError) {
      message.error(AppStrings.CASINO_GAMES_LOAD_ERROR);
    }
  }, [isError]);

  const meta = data?.meta;
  /** UI "Provider" uses subprovider keys from analytics meta. */
  const providerFilter = useMemo(
    () => extractFilterMeta(meta, "subprovider"),
    [meta],
  );
  const categoryFilter = useMemo(
    () => extractFilterMeta(meta, "category"),
    [meta],
  );
  const currencyTypeOptions = useMemo(
    () => parseCasinoKpiFilterMetadata(meta).options,
    [meta],
  );
  const displayRows: CasinoGamesRow[] = useMemo(
    () => (Array.isArray(data?.rows) ? (data.rows as CasinoGamesRow[]) : []),
    [data],
  );
  const displayTotal = data?.total ?? 0;

  const tableEmptyText = useMemo(
    () => data?.emptyText ?? null,
    [data?.emptyText],
  );
  const tableEmptyNode = useMemo(
    () => (
      <Empty
        description={tableEmptyText || AppStrings.NO_DATA}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    ),
    [tableEmptyText],
  );

  const providerOptions: SelectOption[] = useMemo(
    () => toSelectOptions(providerFilter?.options, true),
    [providerFilter?.options],
  );
  const categoryOptions: SelectOption[] = useMemo(
    () => toSelectOptions(categoryFilter?.options, true),
    [categoryFilter?.options],
  );
  const currencySymbol = useCurrencySymbol() ?? "$";
  const columns = useMemo(
    () => getCasinoGamesColumns(sortState, currencySymbol),
    [sortState, currencySymbol],
  );

  const filtersDirty = useMemo(() => {
    return (
      (draftProvider ?? ALL_VALUE) !== (appliedProvider ?? ALL_VALUE) ||
      (draftCategory ?? ALL_VALUE) !== (appliedCategory ?? ALL_VALUE) ||
      JSON.stringify(draftMoneyTypes) !== JSON.stringify(appliedMoneyTypes)
    );
  }, [
    appliedCategory,
    appliedMoneyTypes,
    appliedProvider,
    draftCategory,
    draftMoneyTypes,
    draftProvider,
  ]);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.CASINO, "GAMES");
  }, [setActiveSidebarTab]);

  const handleTableChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<CasinoGamesRow> | SorterResult<CasinoGamesRow>[],
    ) => {
      const one = Array.isArray(sorter) ? sorter[0] : sorter;
      const field = one?.field as CasinoGamesSortKey | undefined;
      setSortState({
        field: field ?? null,
        order: one?.order ?? null,
      });
      setCurrentPage(1);
    },
    [],
  );

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
      setCurrentPage(1);
    },
    [],
  );

  const handleDateRangeQuery = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedProvider(
      draftProvider === undefined || draftProvider === ALL_VALUE
        ? undefined
        : draftProvider,
    );
    setAppliedCategory(
      draftCategory === undefined || draftCategory === ALL_VALUE
        ? undefined
        : draftCategory,
    );
    setAppliedMoneyTypes(draftMoneyTypes);
    setCurrentPage(1);
    message.success(AppStrings.CASINO_GAMES_FILTERS_APPLIED);
  }, [draftCategory, draftMoneyTypes, draftProvider]);

  const handleClearFilters = useCallback(() => {
    setSearchText("");
    setDraftProvider(undefined);
    setAppliedProvider(undefined);
    setDraftCategory(undefined);
    setAppliedCategory(undefined);
    setDraftMoneyTypes([]);
    setAppliedMoneyTypes([]);
    setSortState({ field: null, order: null });
    setCurrentPage(1);
  }, []);

  const handleExport = useCallback(() => {
    if (displayRows.length === 0) {
      message.warning(AppStrings.CASINO_GAMES_EXPORT_PENDING);
      return;
    }
    try {
      const csv = buildCasinoGamesTableCsv(displayRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "casino-games-report.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_err) {
      message.error(AppStrings.CASINO_GAMES_EXPORT_ERROR);
    }
  }, [displayRows]);

  return (
    <>
      <div className="casino-games-page">
        <div className="casino-games-header">
          <div className="casino-games-header-controls manual-actions-header-controls">
            <CustomDatePicker
              dates={dates}
              setDates={setDates}
              getData={handleDateRangeQuery}
              showPresets={true}
              startRange={AppStrings.THIS_MONTH}
            />
            <button
              type="button"
              className="download-button"
              aria-label="Download"
              onClick={handleExport}
            >
              <DownloadOutlined />
            </button>
          </div>
        </div>

        <div className="casino-games-search-filter-row">
          <Input
            variant="search"
            allowClear
            className="casino-games-search-input"
            placeholder={AppStrings.CASINO_GAMES_SEARCH_PLACEHOLDER}
            value={searchText}
            onChange={handleSearchInputChange}
            aria-label={AppStrings.CASINO_GAMES_SEARCH_PLACEHOLDER}
          />
          <button
            type="button"
            className="casino-games-filter-toggle"
            aria-expanded={filtersExpanded}
            aria-label="Toggle filters"
            onClick={() => setFiltersExpanded((v) => !v)}
          >
            <Filter size={18} aria-hidden />
            {filtersExpanded ? (
              <ChevronUp size={18} aria-hidden />
            ) : (
              <ChevronDown size={18} aria-hidden />
            )}
          </button>
        </div>

        {filtersExpanded ? (
          <div className="casino-games-filter-drawer">
            <div className="casino-games-filter-field">
              <span className="casino-games-filter-label">
                {AppStrings.CASINO_KPI_CURRENCY_TYPE}
              </span>
              <SharedSelect<string[]>
                mode="multiple"
                showSearch
                optionFilterProp="label"
                placeholder={AppStrings.CASINO_KPI_ALL_CURRENCY_TYPES}
                className="casino-games-filter-select"
                aria-label={AppStrings.CASINO_KPI_CURRENCY_TYPE}
                value={draftMoneyTypes}
                onChange={(values) =>
                  setDraftMoneyTypes(values.map((value) => String(value)))
                }
                options={currencyTypeOptions}
                maxTagCount="responsive"
              />
            </div>
            <div className="casino-games-filter-field">
              <span className="casino-games-filter-label">
                {AppStrings.CASINO_GAMES_FILTER_PROVIDER_PLACEHOLDER}
              </span>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={AppStrings.CASINO_GAMES_NOT_SELECTED}
                className="casino-games-filter-select"
                aria-label={AppStrings.CASINO_GAMES_FILTER_PROVIDER_PLACEHOLDER}
                value={draftProvider === undefined ? ALL_VALUE : draftProvider}
                onChange={(v) => {
                  setDraftCategory(undefined);
                  setDraftProvider(
                    v == null || v === ALL_VALUE ? undefined : String(v),
                  );
                }}
                options={providerOptions}
              />
            </div>
            <div className="casino-games-filter-field">
              <span className="casino-games-filter-label">
                {AppStrings.CASINO_GAMES_FILTER_CATEGORY_PLACEHOLDER}
              </span>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={AppStrings.CASINO_GAMES_NOT_SELECTED}
                className="casino-games-filter-select"
                aria-label={AppStrings.CASINO_GAMES_FILTER_CATEGORY_PLACEHOLDER}
                value={draftCategory === undefined ? ALL_VALUE : draftCategory}
                onChange={(v) =>
                  setDraftCategory(
                    v == null || v === ALL_VALUE ? undefined : String(v),
                  )
                }
                options={categoryOptions}
              />
            </div>
            <div className="casino-games-filter-actions">
              <button
                type="button"
                className="clear-filters-button"
                onClick={handleClearFilters}
              >
                {AppStrings.CLEAR_FILTERS}
              </button>
              <button
                type="button"
                className="apply-filters-button"
                disabled={!filtersDirty}
                onClick={handleApplyFilters}
              >
                {AppStrings.APPLY}
              </button>
            </div>
          </div>
        ) : null}

        <div className="casino-games-table-section">
          <Spin spinning={isFetching}>
            <DataTable
              id="casino-games-report-table"
              rowKey="key"
              className="casino-finance-table casino-games-table"
              columns={columns}
              dataSource={displayRows}
              pagination={false}
              onChange={handleTableChange}
              scrollPreset="verticalOnly"
              showSorterTooltip={{ target: "sorter-icon" }}
              locale={{ emptyText: tableEmptyNode }}
            />
          </Spin>
          <div className="casino-games-pagination-bar">
            <div className="casino-games-pagination-left">
              <Select
                value={pageSize}
                onChange={(v) => {
                  setPageSize(normalizeAntSelectNumberValue(v));
                  setCurrentPage(1);
                }}
                className="casino-games-page-size-select"
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  value: n,
                  label: String(n),
                }))}
              />
              <span className="casino-games-per-page-label">
                {AppStrings.REPORTING_PAGINATION_PER_PAGE}{" "}
                {AppStrings.CASINO_GAMES_PAGINATION_TOTAL_LABEL.replace(
                  "{total}",
                  String(displayTotal),
                )}
              </span>
            </div>
            <div className="casino-games-pagination-right">
              <Pagination
                current={currentPage}
                total={displayTotal}
                pageSize={pageSize}
                showSizeChanger={false}
                onChange={(p) => setCurrentPage(p)}
                itemRender={(_pageNum, type, originalElement) => {
                  if (type === "prev") {
                    return (
                      <span className="casino-games-pagination-nav">
                        {originalElement}
                        <span>{AppStrings.BACK}</span>
                      </span>
                    );
                  }
                  if (type === "next") {
                    return (
                      <span className="casino-games-pagination-nav">
                        <span>{AppStrings.NEXT}</span>
                        {originalElement}
                      </span>
                    );
                  }
                  return originalElement;
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CasinoGamesReports;
